import { baseUrl, getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier
    };
}

async function legacyFetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(baseUrl + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T | null> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body
    });
    if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

// Types

export interface MainGroup {
    id: number;
    mainGroupCode: string;
    mainGroupDescription: string;
    isVariant: boolean;
    variant1: Variant | null;
    variant2: Variant | null;
    preferredVariantGroup2: VariantGroup | null;
    vat: Vat | null;
    department: Department | null;
    departments: Department[];
    businessArea: BusinessArea | null;
    subCategories: SubCategory[];
    salesChannels: SalesChannel[];
    salesAccount: Account | null;
    discountAccount: Account | null;
    intrastats: Intrastat[];
    brands: Brand[];
    itemGroups: ItemGroup[];
    attributeVisibility: AttributeVisibility;
}

export interface AttributeVisibility {
    intrastat: string;
    meursing: string;
    productHeight: string;
    productLength: string;
    productWeight: string;
    productWidth: string;
    storageLocation: string;
}

export interface Variant {
    id: number;
    variantName: string;
}

export interface VariantGroup {
    id: number;
    groupName: string;
    variant: Variant;
}

export interface Vat {
    id: number;
    vatDescription: string;
}

export interface Department {
    id: number;
    departmentDescription: string;
}

export interface BusinessArea {
    id: number;
    name: string;
}

export interface SubCategory {
    id: number;
    subCategoryCode: string;
    subCategoryName: string;
}

export interface SalesChannel {
    id: number;
    salesChannelCode: string;
    salesChannelDescription: string;
}

export interface Account {
    id: number;
    accountNumber: string;
    accountName: string;
}

export interface Intrastat {
    id: number;
    code: string;
    internalDescription: string;
}

export interface Brand {
    id: number;
    brandCode: string;
    brandDescription: string;
}

export interface ItemGroup {
    brand: Brand;
    showEditForm?: boolean;
}

export interface TenantConfig {
    articleMainGroupCodeLength: number;
    assignedMainGroupCodes: string[];
    variant1: number;
    variant2: number;
}

// API calls

export async function getMainGroup(id: number): Promise<MainGroup> {
    const result = await rms2Fetch<MainGroup>(`maingroups/${id}`);
    if (!result) throw new Error('Empty response for getMainGroup');
    return result;
}

export async function getBrands(): Promise<Brand[]> {
    const response = await rms2Fetch<{ brands: { id: number; brandCode: string; brandDescription: string }[] }>('brands');
    return response?.brands?.map(b => ({ id: b.id, brandCode: b.brandCode, brandDescription: b.brandDescription })) ?? [];
}

export async function getDepartments(): Promise<Department[]> {
    const response = await rms2Fetch<{ departments: { id: number; description: string }[] }>('departments');
    return response?.departments?.map(d => ({ id: d.id, departmentDescription: d.description })) ?? [];
}

export async function getSubCategories(): Promise<SubCategory[]> {
    const response = await rms2Fetch<{ subCategories: SubCategory[] }>('subcategories');
    return response?.subCategories ?? [];
}

export async function getIntrastats(): Promise<Intrastat[]> {
    const response = await rms2Fetch<{ intrastats: Intrastat[] }>('intrastats');
    return response?.intrastats ?? [];
}

export async function getSalesChannels(): Promise<SalesChannel[]> {
    const response = await rms2Fetch<{ salesChannels: SalesChannel[] }>('saleschannels');
    return response?.salesChannels ?? [];
}

export async function getVariants(): Promise<Variant[]> {
    const response = await rms2Fetch<{ variants: { id: number; variantName: string }[] }>('variants');
    return response?.variants ?? [];
}

export async function getVariantGroups(): Promise<VariantGroup[]> {
    const response = await rms2Fetch<{ variantGroups: VariantGroup[] }>('variantgroups');
    return response?.variantGroups ?? [];
}

export async function getBusinessAreas(): Promise<BusinessArea[]> {
    const response = await rms2Fetch<{ businessAreas: BusinessArea[] }>('businessareas');
    return response?.businessAreas ?? [];
}

export async function getTenantConfig(): Promise<TenantConfig> {
    const result = await legacyFetch<TenantConfig>('maingroups/tenantconfig');
    if (!result) throw new Error('Empty response for getTenantConfig');
    return result;
}

export async function getVats(): Promise<Vat[]> {
    const response = await rms2Fetch<{ vats: Vat[] }>('vats');
    return response?.vats ?? [];
}

export async function getAccounts(): Promise<Account[]> {
    const response = await rms2Fetch<{ accounts: Account[] }>('accounts');
    return response?.accounts ?? [];
}

export async function createMainGroup(mainGroup: Partial<MainGroup>, brands: Brand[]): Promise<MainGroup> {
    const result = await rms2Fetch<MainGroup>('maingroups', {
        method: 'POST',
        body: JSON.stringify({
            mainGroupCode: mainGroup.mainGroupCode,
            mainGroupDescription: mainGroup.mainGroupDescription,
            isVariant: mainGroup.isVariant ?? false,
            vatId: mainGroup.vat?.id || null,
            variant1Id: mainGroup.variant1?.id || null,
            variant2Id: mainGroup.variant2?.id || null,
            businessAreaId: mainGroup.businessArea?.id || null,
            salesAccountId: mainGroup.salesAccount?.id || null,
            discountAccountId: mainGroup.discountAccount?.id || null,
            departmentIds: mainGroup.departments?.map(d => d.id) ?? [],
            subCategoryIds: mainGroup.subCategories?.map(s => s.id) ?? [],
            salesChannelIds: mainGroup.salesChannels?.map(s => s.id) ?? [],
            intrastatIds: mainGroup.intrastats?.map(i => i.id) ?? [],
        })
    });
    if (!result) throw new Error('Empty response for createMainGroup');
    return result;
}

export async function updateMainGroup(mainGroup: MainGroup, brands: Brand[]): Promise<MainGroup> {
    const result = await rms2Fetch<MainGroup>(`maingroups/${mainGroup.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            mainGroupDescription: mainGroup.mainGroupDescription,
            isVariant: mainGroup.isVariant,
            vatId: mainGroup.vat?.id || null,
            variant1Id: mainGroup.variant1?.id || null,
            variant2Id: mainGroup.variant2?.id || null,
            businessAreaId: mainGroup.businessArea?.id || null,
            salesAccountId: mainGroup.salesAccount?.id || null,
            discountAccountId: mainGroup.discountAccount?.id || null,
            preferredVariantGroup2Id: mainGroup.preferredVariantGroup2?.id || null,
            departmentIds: mainGroup.departments?.map(d => d.id) ?? [],
            subCategoryIds: mainGroup.subCategories?.map(s => s.id) ?? [],
            salesChannelIds: mainGroup.salesChannels?.map(s => s.id) ?? [],
            intrastatIds: mainGroup.intrastats?.map(i => i.id) ?? [],
            attributeVisibility: mainGroup.attributeVisibility,
        })
    });
    if (!result) throw new Error('Empty response for updateMainGroup');
    return result;
}

export async function createItemGroups(mainGroups: MainGroup[], brands: Brand[]): Promise<void> {
    await rms2Fetch<void>('maingroups/createitemgroups', {
        method: 'POST',
        body: JSON.stringify({
            mainGroupIds: mainGroups.map(mg => mg.id),
            brandIds: brands.map(b => b.id),
        })
    });
}
